import React from 'react';

import Lottie, { AnimationItem } from 'lottie-web';

import { TransactionStatus } from '@api/ecommerce-xp';
import { animations } from '@assets';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useNavigation } from '@navigation';
import { useServiceResponse, useUserInput } from '@store';
import { Anything } from '@types';

export enum SubscriptionStatus {
	success,
	pending,
}

export interface ConclusionHelperResources {
	pendingTitleOne: string;
	pendingTitleTwo: string;
	pendingTitleTwoLink: string;
	pendingTitleTwoUrl: string;
	successTitleOne: string;
	successTitleTwo: string;
	backToHomepage: string;
}

export interface ConclusionHelperOutput {
	resources: ConclusionHelperResources;
	status: SubscriptionStatus;
	animationPendingRef: React.MutableRefObject<Anything>;
	animationSuccessRef: React.MutableRefObject<Anything>;
	onHandleLeave: () => void;
}

export const useConclusionHelper = (): ConclusionHelperOutput => {
	const t = useTranslation();
	const { subscription } = useServiceResponse();
	const { acceptMasterOrVisaCards } = useUserInput();
	const status: SubscriptionStatus =
		subscription?.transactionStatus === TransactionStatus.OK
			? SubscriptionStatus.success
			: SubscriptionStatus.pending;
	const { exit } = useNavigation();

	const animationPendingRef = React.useRef<Anything>(null);
	const animationSuccessRef = React.useRef<Anything>(null);
	const lottie = React.useRef<AnimationItem>();

	const resources: ConclusionHelperResources = {
		pendingTitleOne: t('placeholder.conclusion.pending.titleOne'),
		pendingTitleTwo: t('placeholder.conclusion.pending.titleTwo'),
		pendingTitleTwoLink: t('placeholder.conclusion.pending.titleTwo.link'),
		pendingTitleTwoUrl: t('placeholder.conclusion.pending.titleTwo.url'),
		successTitleOne: t('placeholder.conclusion.success.titleOne'),
		successTitleTwo: t('placeholder.conclusion.success.titleTwo'),
		backToHomepage: t('general.backToHomepage'),
	};

	let animationData: Anything = '';

	if (status === SubscriptionStatus.pending) {
		if (acceptMasterOrVisaCards) {
			animationData = animations.pendingAllMethods;
		} else {
			animationData = animations.pendingMBMethods;
		}
	} else if (status === SubscriptionStatus.success) {
		if (acceptMasterOrVisaCards) {
			animationData = animations.successAllMethods;
		} else {
			animationData = animations.successMBMethods;
		}
	}

	const setAnimation = () => {
		lottie.current = Lottie.loadAnimation({
			container:
				status === SubscriptionStatus.pending ? animationPendingRef.current : animationSuccessRef.current,
			renderer: 'svg',
			loop: false,
			autoplay: false,
			rendererSettings: {
				preserveAspectRatio: 'xMidYMid slice',
			},
			animationData: animationData,
		});

		lottie.current?.goToAndStop(1);
	};

	useDidMount(() => {
		setAnimation();
		lottie.current?.play();
	});

	const onHandleLeave = React.useCallback(() => {
		exit();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { resources, status, animationPendingRef, animationSuccessRef, onHandleLeave };
};
